import React, { useState } from "react";
import { useMultilingual } from "../../../../hooks/useMultilingual";
import { useLanguage } from "../../../../context/LanguageContext";
import ImageModal from "../../../../components/customer/ImageModal/ImageModal";

const ProductInfo = ({ product }) => {
    const multilingual = useMultilingual();
    const { language } = useLanguage();
    const [showDescription, setShowDescription] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);

    // Translations
    const translations = {
        en: {
            description: "Description",
            productDetails: "Product Details",
            defaultDescription:
                "N/A",
        },
        ar: {
            description: "الوصف",
            productDetails: "تفاصيل المنتج",
            defaultDescription:
                "N/A",
        },
    };

    const t = translations[language] || translations.en;

    // Get localized note
    const getProductNote = () => {
        if (!product.note) return null;

        if (typeof product.note === 'string') {
            return product.note;
        }

        if (typeof product.note === 'object') {
            return multilingual.getLocalizedText(product.note);
        }

        return null;
    };

    // Get image title and URL
    const getImageTitle = () => {
        if (!product.imageTitle) return null;

        if (typeof product.imageTitle === 'string') {
            return product.imageTitle;
        }

        if (typeof product.imageTitle === 'object') {
            return multilingual.getLocalizedText(product.imageTitle);
        }

        return null;
    };

    const productNote = getProductNote();
    const imageTitle = getImageTitle();
    const imageUrl = product.imageUrl;

    return (
        <div
            style={{
                borderTop: "1px solid #e5e5e5",
                marginTop: "20px",
                fontFamily: '"Spectral", serif',
            }}
        >
            {/* Important Note Section - Always visible if exists */}
            {productNote && (
                <div
                    style={{
                        padding: "16px 0",
                        borderBottom: "1px solid #e5e5e5",
                        backgroundColor: "var(--themel, #fef9e7)",
                        marginBottom: "0",
                        borderRadius: "6px 6px 0 0"
                    }}
                >
                    <div style={{
                        padding: "0 16px",
                        color: "var(--font, #8b5a00)",
                        fontSize: "14px",
                        lineHeight: "1.5",
                        fontWeight: "500"
                    }}>
                        <strong style={{
                            display: "block",
                            marginBottom: "8px",
                            textTransform: "uppercase",
                            fontSize: "12px",
                            letterSpacing: "0.5px"
                        }}>
                            Important Note
                        </strong>
                        <p style={{
                            margin: 0,
                            whiteSpace: "pre-line"
                        }}>
                            {productNote}
                        </p>
                    </div>
                </div>
            )}

            {/* Image Button Section - Only show if both title and URL exist */}
            {imageTitle && imageUrl && (
                <div style={{ borderBottom: "1px solid #e5e5e5" }}>
                    <button
                        onClick={() => setIsImageModalOpen(true)}
                        style={{
                            width: "100%",
                            padding: "14px 0",
                            background: "none",
                            border: "none",
                            fontSize: "15px",
                            fontWeight: "500",
                            textTransform: "uppercase",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            cursor: "pointer",
                            letterSpacing: "0.5px",
                            transition: "color 0.3s ease",
                            color: "#000",
                        }}
                    >
                        <span style={{ color: "inherit" }}>{imageTitle}</span>
                        <span
                            style={{
                                fontSize: "20px",
                                fontWeight: "300",
                                color: "inherit",
                            }}
                        >
                            +
                        </span>
                    </button>
                </div>
            )}

            {/* Description Section */}
            <div style={{ borderBottom: "1px solid #e5e5e5" }}>
                <button
                    onClick={() => setShowDescription(!showDescription)}
                    style={{
                        width: "100%",
                        padding: "14px 0",
                        background: "none",
                        border: "none",
                        fontSize: "15px",
                        fontWeight: "500",
                        textTransform: "uppercase",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                        letterSpacing: "0.5px",
                        transition: "color 0.3s ease",
                        color: "#000", // Force black color

                    }}
                >
                    <span style={{ color: "inherit" }}>{t.description}</span>
                    <span
                        style={{
                            fontSize: "20px",
                            fontWeight: "300",
                            transition: "transform 0.3s ease",
                            transform: showDescription ? "rotate(180deg)" : "rotate(0deg)",
                            color: "inherit",
                        }}
                    >
                        {showDescription ? "–" : "+"}
                    </span>
                </button>

                <div
                    style={{
                        overflow: "hidden",
                        transition: "all 0.4s ease",
                        maxHeight: showDescription ? "500px" : "0px",
                        opacity: showDescription ? 1 : 0,
                        lineHeight: "1.6",
                        color: "#333",
                        fontSize: "14px",
                        paddingBottom: showDescription ? "12px" : "0px",
                    }}
                >
                    <p style={{ whiteSpace: "pre-line", margin: 0 }}>
                        {multilingual.getProductDescription(product) ||
                            t.defaultDescription}
                    </p>
                </div>
            </div>

            {/* Product Details Section */}
            {product.details && (
                <div style={{ borderBottom: "1px solid #e5e5e5" }}>
                    <button
                        onClick={() => setShowDetails(!showDetails)}
                        style={{
                            width: "100%",
                            padding: "14px 0",
                            background: "none",
                            border: "none",
                            fontSize: "15px",
                            fontWeight: "500",
                            textTransform: "uppercase",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            cursor: "pointer",
                            letterSpacing: "0.5px",
                            transition: "color 0.3s ease",
                        }}
                    >
                        <span>{t.productDetails}</span>
                        <span
                            style={{
                                fontSize: "20px",
                                fontWeight: "300",
                                transition: "transform 0.3s ease",
                                transform: showDetails ? "rotate(180deg)" : "rotate(0deg)",
                            }}
                        >
                            {showDetails ? "–" : "+"}
                        </span>
                    </button>

                    <div
                        style={{
                            overflow: "hidden",
                            transition: "all 0.4s ease",
                            maxHeight: showDetails ? "500px" : "0px",
                            opacity: showDetails ? 1 : 0,
                            lineHeight: "1.6",
                            color: "#333",
                            fontSize: "14px",
                            paddingBottom: showDetails ? "12px" : "0px",
                        }}
                    >
                        <ul style={{ margin: 0, paddingLeft: "20px" }}>
                            {Object.entries(product.details).map(([key, value]) => (
                                <li key={key} style={{ marginBottom: "6px" }}>
                                    <strong>{key}:</strong> {value}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Image Modal */}
            <ImageModal
                isOpen={isImageModalOpen}
                onClose={() => setIsImageModalOpen(false)}
                imageUrl={imageUrl}
                title={imageTitle}
            />
        </div>
    );
};

export default ProductInfo;
